/*
 * om-search-rotator.js — the rotating search placeholder (#467).
 *
 * `Search for "A2 Ghee"` → `Search for "Rava"` → …, the prefix fixed and only the quoted term
 * sliding up and out while the next slides up into place.
 *
 * ── WHY AN OVERLAY AND NOT THE PLACEHOLDER ATTRIBUTE ─────────────────────────────────────────────
 * A native `placeholder` cannot be animated and cannot be split into a fixed half and a moving
 * half, so the rotator paints a span over the field instead. The native placeholder stays in the
 * markup and is only cleared once this file has a working overlay in place, which means the no-JS
 * and until-JS-runs state is whatever the theme already ships (#514 sets it to "Search for organic
 * staples, millets, A2 ghee…"). Nothing is removed from any section template.
 *
 * The overlay is `position: absolute` from the moment it is created, so it never becomes a flex or
 * grid item in the search form and cannot shift the layout — that is the no-CLS requirement, met by
 * construction rather than by measurement.
 *
 * The pure decision functions hang off window.OMSearchRotator so the test suite can exercise them
 * without a browser — same arrangement as assets/om-account.js.
 */
(function () {
  'use strict';

  var OMSearchRotator = {};

  var MIN_HOLD = 1200;      /* below this the term is gone before it can be read */
  var MAX_HOLD = 12000;
  var MAX_TERMS = 12;       /* a rotation nobody sits through is a list nobody curated */
  var DEFAULT_HOLD = 2600;
  var OUT_MS = 340;         /* must match the transition in snippets/om-search-placeholder.liquid */

  /* ── Pure helpers ──────────────────────────────────────────────────────────────────────────── */

  OMSearchRotator.clampHold = function (value, fallback) {
    var fb = Number(fallback);
    if (!isFinite(fb) || fb <= 0) fb = DEFAULT_HOLD;
    fb = Math.min(MAX_HOLD, Math.max(MIN_HOLD, Math.round(fb)));

    var n = Number(value);
    if (!isFinite(n) || n <= 0) return fb;
    return Math.min(MAX_HOLD, Math.max(MIN_HOLD, Math.round(n)));
  };

  /*
   * Takes whatever the metaobject handed over and returns a clean, ordered list. Entries are sorted
   * by `sortOrder` because metaobject `values` come back in no guaranteed order, and anything
   * without one sorts last while keeping its relative position (Array#sort is stable).
   */
  OMSearchRotator.normalizeTerms = function (raw, defaultHold) {
    if (!raw || !raw.length) return [];

    var seen = {};
    var out = [];

    for (var i = 0; i < raw.length; i++) {
      var entry = raw[i];
      var text = typeof entry === 'string' ? entry : (entry && entry.term) || '';
      text = String(text).replace(/\s+/g, ' ').trim();
      if (!text) continue;

      var key = text.toLowerCase();
      if (seen[key]) continue;    /* a repeated term reads as a stutter mid-rotation */
      seen[key] = true;

      var order = Number(entry && entry.sortOrder);
      out.push({
        term: text,
        hold: OMSearchRotator.clampHold(entry && entry.holdMs, defaultHold),
        sortOrder: isFinite(order) ? order : Number.MAX_SAFE_INTEGER
      });
    }

    out.sort(function (a, b) { return a.sortOrder - b.sortOrder; });
    return out.slice(0, MAX_TERMS);
  };

  OMSearchRotator.nextIndex = function (index, length) {
    var len = Number(length);
    if (!isFinite(len) || len <= 0) return 0;
    len = Math.floor(len);

    /*
     * Anything that is not a live index restarts the list rather than coercing to 0 and skipping the
     * first term: `Number(null)` is 0, so coercion cannot tell "no index yet" from "on the first
     * term", and an index past the end means the metaobject list shrank under us.
     */
    var n = typeof index === 'number' && isFinite(index) ? Math.floor(index) : -1;
    if (n < 0 || n >= len) n = -1;

    return (n + 1) % len;
  };

  OMSearchRotator.placeholderFor = function (prefix, term, quotes) {
    var q = quotes && quotes.length === 2 ? quotes : ['“', '”'];
    var p = String(prefix == null ? '' : prefix);
    if (!term) return p.replace(/\s+$/, '');
    return p + q[0] + term + q[1];
  };

  window.OMSearchRotator = OMSearchRotator;

  /* ── DOM ───────────────────────────────────────────────────────────────────────────────────── */

  if (typeof document === 'undefined' || !document.createElement) return;

  var config = null;
  var terms = [];
  var rotators = [];
  var paused = false;

  function readConfig() {
    var el = document.getElementById('om-search-rotator-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || el.innerHTML || '{}');
    } catch (e) {
      return null;    /* a broken config leaves the theme's own placeholder in place */
    }
  }

  function quoted(term) {
    return OMSearchRotator.placeholderFor('', term, config.quotes);
  }

  function attach(input) {
    if (!input || input.getAttribute('data-om-sph') === 'on') return;

    /* A field that is not laid out (the desktop bar on a phone) has no box to paint over. Leave it
       alone; the resize re-scan picks it up if it ever becomes visible. */
    if (!input.offsetWidth && !input.offsetHeight) return;

    var host = input.parentNode;
    if (!host || !host.appendChild) return;

    input.setAttribute('data-om-sph', 'on');
    if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';

    var el = document.createElement('span');
    el.className = 'om-sph';
    el.setAttribute('aria-hidden', 'true');    /* the field keeps its own label; this is decoration */
    el.style.position = 'absolute';            /* set before insertion: never a flex item, never a shift */

    var prefixEl = document.createElement('span');
    prefixEl.className = 'om-sph-prefix';
    prefixEl.textContent = config.prefix;

    var trackEl = document.createElement('span');
    trackEl.className = 'om-sph-track';

    var termEl = document.createElement('span');
    termEl.className = 'om-sph-term';
    termEl.textContent = quoted(terms[0].term);

    trackEl.appendChild(termEl);
    el.appendChild(prefixEl);
    el.appendChild(trackEl);
    host.insertBefore(el, input.nextSibling);

    var original = input.getAttribute('placeholder') || '';
    input.setAttribute('data-om-sph-placeholder', original);
    input.setAttribute('placeholder', '');

    var rotator = {
      input: input,
      el: el,
      termEl: termEl,
      index: 0,
      timer: 0,
      focused: false
    };

    function place() {
      var cs = window.getComputedStyle(input);
      el.style.fontFamily = cs.fontFamily;
      el.style.fontSize = cs.fontSize;
      el.style.fontWeight = cs.fontWeight;
      el.style.fontStyle = cs.fontStyle;
      el.style.letterSpacing = cs.letterSpacing;
      el.style.top = input.offsetTop + 'px';
      el.style.height = input.offsetHeight + 'px';
      el.style.left =
        input.offsetLeft +
        (parseFloat(cs.paddingLeft) || 0) +
        (parseFloat(cs.borderLeftWidth) || 0) + 'px';
      el.style.maxWidth =
        Math.max(0, input.offsetWidth -
          (parseFloat(cs.paddingLeft) || 0) -
          (parseFloat(cs.paddingRight) || 0)) + 'px';
    }

    function sync() {
      /* Hidden while the field holds text — a placeholder that survives typing is just clutter. */
      var hide = input.value !== '' || (!input.offsetWidth && !input.offsetHeight);
      el.style.display = hide ? 'none' : '';
    }

    rotator.place = place;
    rotator.sync = sync;

    input.addEventListener('focus', function () { rotator.focused = true; stop(rotator); });
    input.addEventListener('blur', function () { rotator.focused = false; start(rotator); });
    input.addEventListener('input', sync);

    place();
    sync();
    rotators.push(rotator);
    start(rotator);
  }

  function stop(rotator) {
    if (rotator.timer) {
      window.clearTimeout(rotator.timer);
      rotator.timer = 0;
    }
  }

  function start(rotator) {
    stop(rotator);
    if (paused || rotator.focused || terms.length < 2) return;
    rotator.timer = window.setTimeout(function () { advance(rotator); }, terms[rotator.index].hold);
  }

  function advance(rotator) {
    if (paused || rotator.focused || terms.length < 2) return;

    rotator.index = OMSearchRotator.nextIndex(rotator.index, terms.length);
    var entry = terms[rotator.index];

    rotator.el.classList.add('is-out');
    rotator.timer = window.setTimeout(function () {
      rotator.termEl.textContent = quoted(entry.term);

      /* Jump to the start position with transitions off, flush it, then release: the term rises
         from below rather than fading in where the old one left. */
      rotator.el.classList.remove('is-out');
      rotator.el.classList.add('is-in');
      void rotator.el.offsetWidth;
      rotator.el.classList.remove('is-in');

      start(rotator);
    }, OUT_MS);
  }

  var scanTimer = 0;
  function scan() {
    for (var i = 0; i < config.selectors.length; i++) {
      var found = document.querySelectorAll(config.selectors[i]);
      for (var j = 0; j < found.length; j++) attach(found[j]);
    }
    for (var k = 0; k < rotators.length; k++) {
      rotators[k].place();
      rotators[k].sync();
    }
  }

  function scanSoon() {
    window.clearTimeout(scanTimer);
    scanTimer = window.setTimeout(scan, 150);
  }

  function init() {
    config = readConfig();
    if (!config || !config.selectors || !config.selectors.length) return;

    terms = OMSearchRotator.normalizeTerms(config.terms, config.holdMs);
    if (!terms.length) return;    /* nothing curated: the theme's own placeholder stands */

    config.prefix = config.prefix == null ? 'Search for ' : String(config.prefix);

    scan();

    /* The mobile bar is injected by snippets/om-menu-fix.liquid, which builds it on DOMContentLoaded
       and rebuilds it on resize — so re-scan on the same signals rather than watching the document. */
    window.addEventListener('resize', scanSoon);
    window.setTimeout(scan, 1200);

    document.addEventListener('visibilitychange', function () {
      paused = document.hidden;
      for (var i = 0; i < rotators.length; i++) {
        if (paused) stop(rotators[i]); else start(rotators[i]);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
